const REDACTED = '[Redacted]';
const MAX_STRING_LENGTH = 2_000;
const MAX_ARRAY_ITEMS = 30;
const MAX_OBJECT_KEYS = 50;
const MAX_DEPTH = 6;

const SENSITIVE_KEY_PATTERN =
	/(password|senha|token|authorization|secret|cvv|cvc|card|credit|cpf|cnpj|email|phone|cellphone|telefone|celular|document|otp|verificationCode|recoveryCode|accessToken|refreshToken|apiKey|api_key|holderName|holder_name|cardNumber|card_number|securityCode|pin|bearer)/i;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const truncateString = (value: string) =>
	value.length > MAX_STRING_LENGTH
		? `${value.slice(0, MAX_STRING_LENGTH)}…[truncated]`
		: value;

export const sanitizePayloadForSentry = (
	payload: unknown,
	depth = 0
): unknown => {
	if (payload === null || payload === undefined) {
		return payload;
	}

	if (depth >= MAX_DEPTH) {
		return '[Max depth reached]';
	}

	if (typeof payload === 'string') {
		return truncateString(payload);
	}

	if (typeof payload === 'number' || typeof payload === 'boolean') {
		return payload;
	}

	if (typeof payload === 'bigint' || typeof payload === 'function') {
		return String(payload);
	}

	if (payload instanceof ArrayBuffer) {
		return `[ArrayBuffer ${payload.byteLength} bytes]`;
	}

	if (Array.isArray(payload)) {
		return payload
			.slice(0, MAX_ARRAY_ITEMS)
			.map(item => sanitizePayloadForSentry(item, depth + 1));
	}

	if (isPlainObject(payload)) {
		const entries = Object.entries(payload).slice(0, MAX_OBJECT_KEYS);
		const sanitized: Record<string, unknown> = {};

		for (const [key, value] of entries) {
			sanitized[key] = SENSITIVE_KEY_PATTERN.test(key)
				? REDACTED
				: sanitizePayloadForSentry(value, depth + 1);
		}

		if (Object.keys(payload).length > MAX_OBJECT_KEYS) {
			sanitized.__truncatedKeys = Object.keys(payload).length - MAX_OBJECT_KEYS;
		}

		return sanitized;
	}

	return truncateString(String(payload));
};

export const sanitizeBreadcrumbData = (
	data: Record<string, unknown> | undefined
) => {
	if (!data) {
		return data;
	}

	const sanitized: Record<string, unknown> = {};

	for (const [key, value] of Object.entries(data)) {
		if (
			key === 'requestBody' ||
			key === 'responseBody' ||
			key === 'requestPayload' ||
			key === 'responsePayload'
		) {
			sanitized[key] = sanitizePayloadForSentry(value);
			continue;
		}

		sanitized[key] = value;
	}

	return sanitized;
};
